import { ConditionValidationResult, MentionType } from 'ai-constants'
import { ConditionContext } from 'bff'
import get from 'lodash.get'

type DynamicTextContext = {
  functions: Record<string, unknown>
  inputs: Record<string, unknown>
  inspection: unknown
  variables: Record<string, unknown>
}

type MentionLabelTranslator = (key: string) => string

type RichTextMentionAttrs = {
  mentionLabel: string
  mentionType: MentionType
  mentionValue: string
}

const defaultConditionContext = {
  transformationsByKey: {},
  validationsById: {},
} satisfies ConditionContext

const validationResultTranslationKeyByValue = {
  approved: 'approved',
  disapproved: 'disapproved',
} as const satisfies Record<ConditionValidationResult, string>

const booleanTranslationKeyByValue = {
  false: 'false',
  true: 'true',
} as const satisfies Record<`${boolean}`, string>

const mentionValueResolverByType: {
  [K in MentionType]: (input: {
    attrs: RichTextMentionAttrs
    conditionContext: ConditionContext
    context: DynamicTextContext
    translate: MentionLabelTranslator
  }) => string
} = {
  path: ({ attrs, context }) =>
    coerceMentionValueToString(get(context, attrs.mentionValue)),
  variable: ({ attrs, context }) =>
    coerceMentionValueToString(get(context, attrs.mentionValue)),
  input: ({ attrs, context }) =>
    coerceMentionValueToString(get(context, attrs.mentionValue)),
  function: ({ attrs, context }) =>
    coerceMentionValueToString(get(context, attrs.mentionValue)),
  transformation: ({ attrs, conditionContext, translate }) => {
    const transformation =
      conditionContext.transformationsByKey[attrs.mentionValue]

    if (!transformation) return ''

    if (transformation.datatype === 'boolean') {
      return translate(booleanTranslationKeyByValue[`${transformation.value}`])
    }

    return coerceMentionValueToString(transformation.value)
  },
  validation: ({ attrs, conditionContext, translate }) => {
    const validation = conditionContext.validationsById[attrs.mentionValue]

    if (!validation) return ''

    return translate(validationResultTranslationKeyByValue[validation.value])
  },
}

const coerceMentionValueToString = (value: unknown) => {
  if (typeof value === 'string') return value
  if (typeof value === 'number') return value.toString()
  if (typeof value === 'boolean') return value.toString()

  if (value === null || typeof value === 'undefined') return ''

  return value.toString()
}

export const resolveRichTextMentionValue = ({
  attrs,
  conditionContext = defaultConditionContext,
  context,
  translate,
}: {
  attrs: RichTextMentionAttrs
  conditionContext?: ConditionContext
  context: DynamicTextContext
  translate: MentionLabelTranslator
}) =>
  mentionValueResolverByType[attrs.mentionType]({
    attrs,
    conditionContext,
    context,
    translate,
  })
